import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo } from 'react';
import cn from 'classnames';
import { Box, BottomSheetContentLayout, Column, Row, Typography, useOverlayContext } from 'seagull';
import { ChevronDown, ChevronUp, EmailSent, Info } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { useLocaleKeys } from '~/i18n';
import styles from './NotificationActivityLogEntry.module.scss';
import { renderHeadline } from './notificationHeadlines';
import { NotificationPreviewBox } from './NotificationPreviewBox';
export const GetNotificationActivityLogEntryTID = (parentTestId)=>getTestIds(parentTestId);
const bodyOf = (delivery)=>delivery.channel.type === 'email' ? delivery.channel.email.body : delivery.channel.sms.body;
export const NotificationActivityLogEntry = (param)=>{
    let { entry, isExpanded, onToggleExpand, vendorLogoUrl, testId = 'notification-activity-log-entry', isMobile } = param;
    const { t } = useLocaleKeys();
    const testIds = useMemo(()=>GetNotificationActivityLogEntryTID(testId), [
        testId
    ]);
    const { showBottomSheet, hideBottomSheet } = useOverlayContext();
    const deliveries = entry.deliveries();
    const previewDeliveries = useMemo(()=>{
        const firstEmail = deliveries.find((d)=>d.channel.type === 'email' && bodyOf(d).length > 0);
        const firstSms = deliveries.find((d)=>d.channel.type === 'sms' && bodyOf(d).length > 0);
        return [
            firstEmail,
            firstSms
        ].filter((d)=>d != null);
    }, [
        deliveries
    ]);
    const canExpand = previewDeliveries.length > 0;
    useEffect(()=>{
        if (!isExpanded || isMobile) return undefined;
        const onKeyDown = (e)=>{
            if (e.key === 'Escape') {
                onToggleExpand(null);
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return ()=>window.removeEventListener('keydown', onKeyDown);
    }, [
        isExpanded,
        onToggleExpand,
        isMobile
    ]);
    const openMobileSheet = ()=>{
        showBottomSheet({
            content: /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
                header: {
                    title: /*#__PURE__*/ _jsx(Box, {
                        as: "span",
                        style: {
                            display: 'contents'
                        },
                        onClick: (e)=>{
                            if (e.target.closest('a')) hideBottomSheet();
                        },
                        children: renderHeadline(entry, t)
                    })
                },
                contentGutter: "m",
                children: /*#__PURE__*/ _jsx(Column, {
                    gap: 12,
                    onClick: (e)=>{
                        if (e.target.closest('a')) hideBottomSheet();
                    },
                    children: previewDeliveries.map((d, index)=>/*#__PURE__*/ _jsx(NotificationPreviewBox, {
                            entry: entry,
                            delivery: d,
                            vendorLogoUrl: vendorLogoUrl,
                            emailTestId: testIds.NotificationActivityLogEntryTestIds.EmailPreview(index),
                            smsTestId: testIds.NotificationActivityLogEntryTestIds.SmsPreview(index),
                            inRow: true
                        }, "".concat(d.notificationId, "-").concat(index)))
                })
            })
        });
    };
    const handleHeaderClick = (e)=>{
        if (!canExpand) return;
        if (e.target.closest('a')) return;
        if (isMobile) {
            openMobileSheet();
            return;
        }
        onToggleExpand(isExpanded ? null : entry.id());
    };
    return /*#__PURE__*/ _jsxs(Box, {
        testId: testIds.NotificationActivityLogEntryTestIds.Root(),
        className: styles.root,
        children: [
            /*#__PURE__*/ _jsxs(Row, {
                testId: testIds.NotificationActivityLogEntryTestIds.Header(),
                alignItems: "center",
                gap: 12,
                className: cn(styles.header, {
                    [styles.notExpandable]: !canExpand
                }),
                onClick: handleHeaderClick,
                role: "button",
                "aria-expanded": isExpanded,
                "aria-disabled": !canExpand,
                children: [
                    /*#__PURE__*/ _jsx(Box, {
                        testId: testIds.NotificationActivityLogEntryTestIds.Icon(),
                        as: "span",
                        className: styles.icon,
                        "aria-hidden": true,
                        children: /*#__PURE__*/ _jsx(EmailSent, {})
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        as: "span",
                        testId: testIds.NotificationActivityLogEntryTestIds.Headline(),
                        className: styles.headline,
                        children: renderHeadline(entry, t)
                    }),
                    canExpand && /*#__PURE__*/ _jsx(Box, {
                        testId: testIds.NotificationActivityLogEntryTestIds.Chevron(),
                        as: "span",
                        className: styles.chevron,
                        "aria-hidden": true,
                        children: isMobile ? /*#__PURE__*/ _jsx(Info, {}) : isExpanded ? /*#__PURE__*/ _jsx(ChevronUp, {}) : /*#__PURE__*/ _jsx(ChevronDown, {})
                    })
                ]
            }),
            !isMobile && isExpanded && canExpand && previewDeliveries.length === 1 && /*#__PURE__*/ _jsx(NotificationPreviewBox, {
                entry: entry,
                delivery: previewDeliveries[0],
                vendorLogoUrl: vendorLogoUrl,
                emailTestId: testIds.NotificationActivityLogEntryTestIds.EmailPreview(0),
                smsTestId: testIds.NotificationActivityLogEntryTestIds.SmsPreview(0)
            }),
            !isMobile && isExpanded && canExpand && previewDeliveries.length > 1 && /*#__PURE__*/ _jsx(Row, {
                alignItems: "stretch",
                gap: 12,
                className: styles.previewsRow,
                children: previewDeliveries.map((d, index)=>/*#__PURE__*/ _jsx(NotificationPreviewBox, {
                        entry: entry,
                        delivery: d,
                        vendorLogoUrl: vendorLogoUrl,
                        emailTestId: testIds.NotificationActivityLogEntryTestIds.EmailPreview(index),
                        smsTestId: testIds.NotificationActivityLogEntryTestIds.SmsPreview(index),
                        inRow: true
                    }, "".concat(d.notificationId, "-").concat(index)))
            })
        ]
    });
};
